import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, X, Loader2, ImagePlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fase 13/22 · Uploader de fotos multi-select con compresión inteligente.
 * - endpoint: URL relativa al backend (ej `/marketplace/photos` o `/mercadolibre/photos`)
 * - value: array de URLs (controlled)
 * - onChange(urls[])
 * - maxSizeMb: 10 por defecto (Fase 22: útil para fotografía inmobiliaria)
 * - mobileCamera: true → añade botón separado con capture="environment"
 *
 * Compresión cliente-side: si supera 2 MB o dimensiones > 2048 px, se
 * redimensiona a 2048 px lado mayor con calidad JPEG 85%. Mantiene calidad
 * profesional pero baja peso ~5x. Backend valida ≤10 MB además.
 */

async function _compressImage(file, { maxSide = 2048, quality = 0.85 } = {}) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  // Solo comprimir si vale la pena (>1.5MB o probable dimensión grande)
  if (file.size < 1.5 * 1024 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file; // no ganamos nada
    // Nuevo File con nombre saneado
    const name = file.name.replace(/\.(png|webp|jpg|jpeg)$/i, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch (_) {
    return file;
  }
}

export default function PhotoUploader({
  endpoint,
  value = [],
  onChange,
  maxSizeMb = 10,
  mobileCamera = true,
  testid = "photo-uploader",
}) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const absoluteUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    // pre-validación de tamaño local (backend re-valida)
    for (const f of arr) {
      if (f.size > maxSizeMb * 1024 * 1024) {
        toast.error(`FStudy: "${f.name}" supera ${maxSizeMb} MB.`);
        return;
      }
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        const compressed = await _compressImage(f);
        const reduced = compressed.size < f.size;
        const fd = new FormData();
        fd.append("file", compressed);
        const { data } = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
        if (reduced) {
          const pct = Math.round((1 - compressed.size / f.size) * 100);
          toast.message(`Comprimida ${pct}% (${(compressed.size / 1024 / 1024).toFixed(1)} MB)`);
        }
      }
      onChange([...value, ...uploaded]);
      toast.success(uploaded.length === 1 ? "Foto agregada" : `${uploaded.length} fotos agregadas`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const removePhoto = (url) => onChange(value.filter((u) => u !== url));

  return (
    <div className="space-y-2" data-testid={testid}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={(e) => uploadFiles(e.target.files)}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {mobileCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => uploadFiles(e.target.files)}
          className="hidden"
          data-testid={`${testid}-camera-input`}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {value.map((url) => (
          <div key={url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border group" data-testid={`${testid}-thumb`}>
            <img src={absoluteUrl(url)} alt="foto" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(url)}
              className="absolute top-1 right-1 bg-brand-ink/70 hover:bg-brand-ink text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Quitar foto"
              data-testid={`${testid}-remove`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-20 w-20 flex flex-col items-center justify-center gap-1 text-stone-500 hover:text-brand-emerald-dark hover:border-brand-emerald"
          data-testid={`${testid}-add-btn`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          <span className="text-[10px]">Fotos</span>
        </Button>
        {mobileCamera && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            className="h-20 w-20 flex flex-col items-center justify-center gap-1 text-stone-500 hover:text-brand-emerald-dark hover:border-brand-emerald sm:hidden"
            data-testid={`${testid}-camera-btn`}
          >
            <Camera className="h-4 w-4" />
            <span className="text-[10px]">Cámara</span>
          </Button>
        )}
      </div>
    </div>
  );
}
